import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const CustomerFeedback = () => {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [existingReview, setExistingReview] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    setIsAuthenticated(!!token);

    const fetchTicketAndReview = async () => {
      setLoading(true);
      try {
        const apiUrl = `https://customersupport-jhy9.onrender.com/api/support-ticket/${ticketId}`;
        
        const ticketResponse = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!ticketResponse.ok) {
          if (ticketResponse.status === 404) {
            throw new Error(`Ticket with ID ${ticketId} not found. Please check the URL.`);
          }
          throw new Error(`Failed to fetch ticket: ${ticketResponse.status} ${ticketResponse.statusText}`);
        }
        
        const ticketData = await ticketResponse.json();
        
        let processedTicket = null;
        
        if (ticketData && ticketData._id) {
          processedTicket = ticketData;
        } else if (ticketData && ticketData.data && typeof ticketData.data === 'object') {
          processedTicket = ticketData.data;
        } else if (ticketData && ticketData.ticket && typeof ticketData.ticket === 'object') {
          processedTicket = ticketData.ticket;
        } else if (Array.isArray(ticketData) && ticketData.length > 0) {
          processedTicket = ticketData[0];
        } else if (ticketData && typeof ticketData === 'object') {
          processedTicket = ticketData;
        }
        
        if (!processedTicket || typeof processedTicket !== 'object') {
          throw new Error('No valid ticket data found in API response');
        }
        
        setTicket(processedTicket);

        // Check for existing review
        try {
          const headers = {
            'Content-Type': 'application/json'
          };
          
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const reviewResponse = await fetch(
            `https://customersupport-jhy9.onrender.com/api/ticket-review/${ticketId}`,
            { headers }
          );

          if (reviewResponse.ok) {
            const reviewData = await reviewResponse.json();
            
            let processedReview = reviewData;
            if (reviewData.data) {
              processedReview = reviewData.data;
            } else if (Array.isArray(reviewData) && reviewData.length > 0) {
              processedReview = reviewData[0];
            }
            
            setExistingReview(processedReview);
            setRating(processedReview.rating || 0);
            setComment(processedReview.comment || '');
          }
        } catch (reviewErr) {
          // Silently fail review fetch as it's optional
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (ticketId) {
      fetchTicketAndReview();
    } else {
      setLoading(false);
      setError('No ticket ID provided in URL');
    }
  }, [ticketId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!ticket) {
      setError('Ticket data not loaded. Please refresh the page.');
      return;
    }

    const ticketStatus = ticket.status ? ticket.status.toLowerCase().trim() : '';
    if (!ticketStatus || !['resolved', 'closed' , 'in progress'].includes(ticketStatus)) {
      const statusMessage = !ticketStatus 
        ? 'Ticket status is not available' 
        : `Current status: "${ticket.status}"`;
      setError(`Cannot submit review. ${statusMessage}. Only resolved or closed tickets can be reviewed.`);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem("authToken");
      
      const customerId = ticket.customerId;
      if (!customerId) {
        throw new Error("Customer ID not found in ticket data");
      }

      const assignedTo = ticket.assignedTo || null;

      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const payload = {
        ticketId,
        rating,
        comment,
        customerId,
        assignedTo
      };

      const response = await fetch('https://customersupport-jhy9.onrender.com/api/ticket-review', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to submit review: ${response.status}`);
      }

      const reviewData = await response.json();
      
      let processedReview = reviewData;
      if (reviewData.data) {
        processedReview = reviewData.data;
      }
      
      setExistingReview(processedReview);
      setSubmitted(true);

    } catch (err) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    setError('');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="feedback-container">
        <div className="feedback-card">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  // Show success message if submitted
  if (submitted) {
    return (
      <div className="feedback-container">
        <div className="feedback-card success-card">
          <div className="success-icon">
            <svg className="checkmark" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="success-title">Thank You!</h2>
          <p className="success-message">Your feedback has been submitted successfully.</p>
          <div className="button-group">
            <button 
              onClick={() => {
                setSubmitted(false);
                setRating(existingReview?.rating || 0);
                setComment(existingReview?.comment || '');
              }}
              className="secondary-button"
            >
              View Feedback
            </button>
            <button 
              onClick={() => window.close()}
              className="primary-button"
            >
              Close
            </button>
          </div>
        </div>
        
        <style jsx>{`
          .success-card {
            text-align: center;
            padding: 2.5rem;
          }
          
          .success-icon {
            width: 4rem;
            height: 4rem;
            background-color: #d1fae5;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
          }
          
          .checkmark {
            width: 2rem;
            height: 2rem;
            color: #059669;
          }
          
          .success-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 0.5rem;
          }
          
          .success-message {
            color: #6b7280;
            margin-bottom: 1.5rem;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="feedback-container">
      <div className="feedback-card">
        <div className="card-header">
          <h1 className="card-title">
            {existingReview ? 'Update Feedback' : 'Service Feedback'}
          </h1>
          {/* {!isAuthenticated && (
            <p className="card-subtitle">
              No login required
            </p>
          )} */}
        </div>
        
        <div className="card-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {existingReview && (
            <div className="info-message success">
              <p className="info-title">✓ You have already reviewed this ticket</p>
              <p>You can update your feedback below.</p>
            </div>
          )}

          {/* {!isAuthenticated && (
            <div className="info-message">
              <p className="info-title">ℹ️ Anonymous feedback</p>
              <p>You can provide feedback without logging in.</p>
            </div>
          )} */}
          
          {ticket ? (
            <div className="ticket-info">
              <h2 className="ticket-title">
                Ticket #{(ticket._id || ticket.id || ticketId).slice(-6)}
              </h2>
              <p className="ticket-subject">{ticket.subject || 'No subject'}</p>
              <p className="ticket-description">{ticket.description || 'No description'}</p>
              
              {ticket.status ? (
                <div className="status-container">
                  <span className={`status-badge ${ticket.status.toLowerCase()}`}>
                    {ticket.status.toUpperCase()}
                  </span>
                  
                  {ticket.status && !['resolved', 'closed' , 'in progress'].includes(ticket.status.toLowerCase()) && (
                    <div className="warning-message">
                      ⚠️ Reviews can only be submitted for resolved or closed tickets
                    </div>
                  )}
                </div>
              ) : (
                <div className="status-container">
                  <span className="status-badge error">
                    STATUS NOT AVAILABLE
                  </span>
                  <div className="error-message small">
                    ❌ Cannot determine ticket status. Reviews may not be allowed.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="ticket-info loading">
              <h2 className="ticket-title">
                Ticket #{ticketId?.slice(-6) || 'Loading...'}
              </h2>
              <div className="loading-skeleton">
                <div className="skeleton-line"></div>
                <div className="skeleton-line medium"></div>
                <div className="skeleton-line short"></div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="feedback-form">
            <div className="rating-container">
              <label className="rating-label">
                How would you rate our service?
                {existingReview && <span className="current-rating">(Current: {existingReview.rating}/5)</span>}
              </label>
              <div className="stars-container">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`star ${rating >= star ? 'selected' : ''}`}
                    onClick={() => handleRatingChange(star)}
                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              {rating > 0 && (
                <div className="rating-selected">
                  Selected: {rating}/5 stars
                </div>
              )}
            </div>
            
            <div className="comment-container">
              <label htmlFor="comment" className="comment-label">
                Additional comments (optional)
              </label>
              <textarea
                id="comment"
                rows="4"
                className="comment-textarea"
                placeholder="Tell us about your experience..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              ></textarea>
            </div>
            
            <button
              type="submit"
              disabled={submitting || rating === 0 || !ticket}
              className={`submit-button ${submitting || rating === 0 || !ticket ? 'disabled' : ''}`}
            >
              {submitting ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  {existingReview ? 'Updating...' : 'Submitting...'}
                </span>
              ) : (
                existingReview ? 'Update Feedback' : 'Submit Feedback'
              )}
            </button>
          </form>

          {existingReview && !submitted && (
            <div className="previous-review">
              <h4 className="review-title">Your Previous Review:</h4>
              <div className="review-content">
                <div className="review-rating">
                  <span className="stars">
                    {'★'.repeat(existingReview.rating)}
                  </span>
                  <span className="rating-text">
                    {existingReview.rating}/5 stars
                  </span>
                </div>
                {existingReview.comment && (
                  <p className="review-comment">
                    "{existingReview.comment}"
                  </p>
                )}
                <div className="review-date">
                  Reviewed on: {existingReview.reviewedAt ? new Date(existingReview.reviewedAt).toLocaleDateString() : 'Date not available'}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="card-footer">
          <p className="footer-text">
            Your feedback helps us improve our services. Thank you for taking the time to share your experience.
          </p>
        </div>
      </div>
      
      <style jsx>{`
        .feedback-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f9ff 0%, #f0fdf4 100%);
          padding: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }
        
        .feedback-card {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          max-width: 28rem;
          width: 100%;
        }
        
        .card-header {
          background: #2563eb;
          padding: 1.5rem;
          text-align: center;
        }
        
        .card-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin: 0 0 0.25rem 0;
        }
        
        .card-subtitle {
          color: #bfdbfe;
          font-size: 0.875rem;
          margin: 0;
        }
        
        .card-content {
          padding: 1.5rem;
        }
        
        .error-message {
          background-color: #fef2f2;
          color: #dc2626;
          padding: 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }
        
        .error-message.small {
          font-size: 0.75rem;
          padding: 0.5rem;
          margin-top: 0.5rem;
        }
        
        .info-message {
          background-color: #dbeafe;
          color: #1e40af;
          padding: 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }
        
        .info-message.success {
          background-color: #d1fae5;
          color: #065f46;
        }
        
        .info-title {
          font-weight: 600;
          margin: 0 0 0.25rem 0;
        }
        
        .ticket-info {
          margin-bottom: 1.5rem;
        }
        
        .ticket-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }
        
        .ticket-subject {
          color: #4b5563;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }
        
        .ticket-description {
          color: #6b7280;
          font-size: 0.875rem;
          margin-bottom: 0.75rem;
        }
        
        .status-container {
          margin-top: 0.75rem;
        }
        
        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        
        .status-badge.resolved {
          background-color: #bbf7d0;
          color: #166534;
        }
        
        .status-badge.closed {
          background-color: #e5e7eb;
          color: #374151;
        }
        
        .status-badge.in-progress, .status-badge['in progress'] {
          background-color: #fef3c7;
          color: #92400e;
        }
        
        .status-badge.error {
          background-color: #fecaca;
          color: #dc2626;
        }
        
        .status-badge:not(.resolved):not(.closed):not(.error):not(.in-progress):not(['in progress']) {
          background-color: #bfdbfe;
          color: #1e40af;
        }
        
        .warning-message {
          background-color: #fffbeb;
          color: #92400e;
          padding: 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          margin-top: 0.5rem;
        }
        
        .loading-skeleton {
          margin-top: 0.5rem;
        }
        
        .skeleton-line {
          height: 1rem;
          background-color: #e5e7eb;
          border-radius: 0.25rem;
          margin-bottom: 0.5rem;
          animation: pulse 2s infinite;
        }
        
        .skeleton-line.medium {
          width: 75%;
        }
        
        .skeleton-line.short {
          width: 50%;
        }
        
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        
        .feedback-form {
          margin-top: 1.5rem;
        }
        
        .rating-container {
          margin-bottom: 1.5rem;
        }
        
        .rating-label {
          display: block;
          color: #374151;
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 0.75rem;
        }
        
        .current-rating {
          color: #2563eb;
          margin-left: 0.25rem;
        }
        
        .stars-container {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        
        .star {
          width: 3rem;
          height: 3rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .star.selected {
          color: #f59e0b;
          transform: scale(1.1);
        }
        
        .star:not(.selected) {
          color: #d1d5db;
        }
        
        .star:not(.selected):hover {
          color: #fbbf24;
        }
        
        .rating-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: #9ca3af;
          margin-bottom: 0.5rem;
        }
        
        .rating-selected {
          text-align: center;
          font-size: 0.875rem;
          color: #4b5563;
          margin-top: 0.5rem;
        }
        
        .comment-container {
          margin-bottom: 1.5rem;
        }
        
        .comment-label {
          display: block;
          color: #374151;
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
        }
        
        .comment-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-family: inherit;
          font-size: 0.875rem;
          transition: all 0.2s;
        }
        
        .comment-textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        
        .submit-button {
          width: 100%;
          padding: 0.75rem 1rem;
          background-color: #2563eb;
          color: white;
          font-weight: 600;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .submit-button:not(.disabled):hover {
          background-color: #1d4ed8;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        }
        
        .submit-button.disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
        }
        
        .button-loading {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .previous-review {
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }
        
        .review-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }
        
        .review-content {
          background-color: #f9fafb;
          padding: 0.75rem;
          border-radius: 0.5rem;
        }
        
        .review-rating {
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        
        .stars {
          color: #f59e0b;
          margin-right: 0.5rem;
        }
        
        .rating-text {
          color: #6b7280;
          font-size: 0.875rem;
        }
        
        .review-comment {
          color: #6b7280;
          font-size: 0.875rem;
          font-style: italic;
          margin: 0 0 0.5rem 0;
        }
        
        .review-date {
          color: #9ca3af;
          font-size: 0.75rem;
        }
        
        .card-footer {
          background-color: #f9fafb;
          padding: 1rem 1.5rem;
          text-align: center;
        }
        
        .footer-text {
          color: #6b7280;
          font-size: 0.75rem;
          margin: 0;
        }
        
        .loading-spinner {
          width: 3rem;
          height: 3rem;
          border: 3px solid #e5e7eb;
          border-top: 3px solid #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }
        
        .loading-text {
          color: #6b7280;
          text-align: center;
        }
        
        .button-group {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
        }
        
        .primary-button {
          background-color: #2563eb;
          color: white;
          font-weight: 600;
          padding: 0.5rem 1.5rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .primary-button:hover {
          background-color: #1d4ed8;
        }
        
        .secondary-button {
          background-color: #4b5563;
          color: white;
          font-weight: 600;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .secondary-button:hover {
          background-color: #374151;
        }
      `}</style>
    </div>
  );
};

export default CustomerFeedback;